import React, { useState, useEffect } from 'react';
import { 
    Search, Calendar, Loader2, Plus, Trash2, Edit2, 
    ExternalLink, Eye, EyeOff, Brain, Trophy, ChevronLeft, ChevronRight, X, History as HistoryIcon
} from 'lucide-react';
import { supabase } from '../../../services/authService';
import { fetchLeagues, fetchGamesByLeagueAndDate } from '../../../services/footballService';
import { translateNewsContent } from '../../../services/geminiService';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { Game, LeagueDB } from '../../../types';
import { at } from '../../../i18nAdmin';

interface AnalysisTabProps {
    isOpen: boolean;
    onClose: () => void;
    editData?: any;
    onSuccess?: () => void;
}

export const AnalysisModal: React.FC<AnalysisTabProps> = ({ isOpen, onClose, editData, onSuccess }) => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const { user } = useAuth();
    
    const [leagues, setLeagues] = useState<LeagueDB[]>([]);
    const [selectedLeague, setSelectedLeague] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [matches, setMatches] = useState<Game[]>([]);
    const [loadingMatches, setLoadingMatches] = useState(false);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<Game | null>(null);
    const [editId, setEditId] = useState<string | null>(null);
    const [dbContent, setDbContent] = useState<any>(null);
    const [contentKo, setContentKo] = useState('');
    const [predictedWinner, setPredictedWinner] = useState('');
    const [predictedScore, setPredictedScore] = useState('');
    const [winProbability, setWinProbability] = useState<number>(50);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [analysisList, setAnalysisList] = useState<any[]>([]);
    const [loadingList, setLoadingList] = useState(true);

    useEffect(() => {
        const loadLeagues = async () => {
            const data = await fetchLeagues(true);
            setLeagues(data);
            if (data.length > 0) setSelectedLeague(data[0].id);
        };
        loadLeagues();
    }, []);

    useEffect(() => {
        const fetchFullContent = async () => {
            if (isOpen && editData) {
                setEditId(editData.id);
                setDbContent(editData);
                
                if (editData.post_id) {
                    // This is from match_predictions table
                    setContentKo(editData.analysis_reason_ko || '');
                    setPredictedWinner(editData.predicted_winner || '');
                    setPredictedScore(editData.predicted_score || '');
                    setWinProbability(editData.win_probability || 50);
                } else {
                    // This is from news_list table
                    try {
                        const { data, error } = await supabase
                            .from('news_list')
                            .select('*')
                            .eq('id', editData.id)
                            .single();
                        
                        if (data && !error) {
                            setContentKo(data.content_ko || '');
                            setDbContent(data);
                        } else {
                            setContentKo(editData.content || editData.content_ko || '');
                        }
                    } catch (e) {
                        console.error("Failed to fetch full analysis content:", e);
                    }
                }
            } else if (isOpen && !editData) {
                setEditId(null);
                setDbContent(null);
                resetForm();
            }
        };

        fetchFullContent();
    }, [isOpen, editData]);

    const loadAnalysisList = async () => {
        setLoadingList(true);
        try {
            const { data, error } = await supabase
                .from('news_list')
                .select('*')
                .eq('type', 'analysis')
                .order('created_at', { ascending: false });
            if (error) throw error;
            setAnalysisList(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    };

    const handleOpenModal = () => {
        resetForm();
        setIsModalOpen(true);
        setTimeout(() => setIsAnimating(true), 50);
    };

    const handleCloseModal = () => {
        setIsAnimating(false);
        setTimeout(() => setIsModalOpen(false), 500);
    };

    const handleSearchMatches = async () => {
        if (!selectedLeague || !selectedDate) return;
        setLoadingMatches(true);
        try {
            const data = await fetchGamesByLeagueAndDate(selectedLeague, selectedDate);
            setMatches(data);
        } catch (e) {
            showToast('error', 'Failed to fetch matches');
        } finally {
            setLoadingMatches(false);
        }
    };

    const handleSubmit = async () => {
        if ((!selectedMatch && !editId) || !contentKo || !predictedWinner || !predictedScore) {
            showToast('error', '모든 필드를 입력해주세요');
            return;
        }

        setIsSubmitting(true);
        try {
            // Use Gemini for translation if needed
            const translations = await translateNewsContent('', '', contentKo);

            const predictionPayload: any = {
                post_id: selectedMatch ? selectedMatch.id : (editData?.post_id || 'manual'),
                user_id: user?.id,
                predicted_winner: predictedWinner,
                predicted_score: predictedScore,
                analysis_reason: translations?.summary_en || contentKo, // Fallback to contentKo if translation fails
                analysis_reason_ko: contentKo,
                win_probability: winProbability,
                updated_at: new Date().toISOString(),
                // ...(translations ? {
                //     analysis_reason_ja: translations.summary_ja,
                //     analysis_reason_es: translations.summary_es,
                //     analysis_reason_fr: translations.summary_fr
                // } : {})
            };

            if (selectedMatch) {
                predictionPayload.home_team_id = String(selectedMatch.homeTeam.id);
                predictionPayload.away_team_id = String(selectedMatch.awayTeam.id);
                predictionPayload.league_id = String(selectedMatch.league);
                // Parse matchdate correctly
                predictionPayload.matchdate = new Date(`${selectedDate}T${selectedMatch.time || '12:00'}:00`).toISOString();
            }

            let error;
            if (editId && editData?.post_id) {
                // Update match_predictions
                ({ error } = await supabase.from('match_predictions').update(predictionPayload).eq('id', editId));
            } else {
                // Insert into match_predictions
                predictionPayload.created_at = new Date().toISOString();
                ({ error } = await supabase.from('match_predictions').insert(predictionPayload));
            }

            if (error) throw error;

            // Also maintain news_list for backward compatibility if needed, 
            // but the requirement is to link to the new DB.
            // For now, let's keep the news_list logic as well if it was intended to be both.
            // If the user only wants match_predictions, we can skip news_list.
            
            showToast('success', '경기 분석이 저장되었습니다.');
            onSuccess?.();
            onClose();
        } catch (e: any) {
            showToast('error', e.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setContentKo('');
        setPredictedWinner('');
        setPredictedScore('');
        setWinProbability(50);
        setSelectedMatch(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('정말 삭제하시겠습니까?')) return;
        try {
            const { error } = await supabase.from('news_list').delete().eq('id', id);
            if (error) throw error;
            showToast('success', '삭제되었습니다');
            loadAnalysisList();
        } catch (e: any) {
            showToast('error', e.message);
        }
    };

    return (
        <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className={`relative bg-white w-full max-w-4xl rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden flex flex-col h-[90vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
                <header className="px-8 py-6 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center">
                            {editId ? <Edit2 size={24} /> : <Plus size={24} />}
                        </div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{editId ? '경기 분석 수정' : '새 경기 분석 등록'}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors">
                        <X size={28} />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8 bg-slate-50/30">
                   
                    {!editId && (
                        <div className="space-y-4">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">대상 경기 조회</label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <select 
                                    className="bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                                    value={selectedLeague}
                                    onChange={e => setSelectedLeague(e.target.value)}
                                >
                                    {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                                <input 
                                    type="date" 
                                    className="bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                                    value={selectedDate}
                                    onChange={e => setSelectedDate(e.target.value)}
                                />
                                <button 
                                    onClick={handleSearchMatches}
                                    disabled={loadingMatches}
                                    className="bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest md:hover:bg-black transition-all disabled:opacity-50 h-[46px]"
                                >
                                    {loadingMatches ? <Loader2 className="animate-spin mx-auto" size={18} /> : '경기 조회'}
                                </button>
                            </div>
                        </div>
                    )}

                    {matches.length > 0 && !editId && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {matches.map(m => (
                                        <button 
                                            key={m.id}
                                            onClick={() => setSelectedMatch(m)}
                                            className={`p-4 rounded-2xl border text-left transition-all ${selectedMatch?.id === m.id ? 'border-rose-500 bg-rose-50/30 ring-1 ring-rose-500' : 'border-slate-100 bg-slate-50 md:hover:border-slate-200'}`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex flex-col items-center gap-1 shrink-0">
                                                    <img src={m.homeTeam.logo} className="w-8 h-8 object-contain" alt="" />
                                                    <span className="text-[8px] font-black uppercase truncate max-w-[60px]">{m.homeTeam.name}</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{m.time}</span>
                                                    <span className="text-xs font-black text-slate-900 italic">VS</span>
                                                </div>
                                                <div className="flex flex-col items-center gap-1 shrink-0">
                                                    <img src={m.awayTeam.logo} className="w-8 h-8 object-contain" alt="" />
                                                    <span className="text-[8px] font-black uppercase truncate max-w-[60px]">{m.awayTeam.name}</span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                    <div className="space-y-6 border-t border-slate-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">예상 승리팀</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                    value={predictedWinner}
                                    onChange={e => setPredictedWinner(e.target.value)}
                                >
                                    <option value="">선택하세요</option>
                                    {selectedMatch ? (
                                        <>
                                            <option value={selectedMatch.homeTeam.name}>{selectedMatch.homeTeam.name} (홈)</option>
                                            <option value={selectedMatch.awayTeam.name}>{selectedMatch.awayTeam.name} (어웨이)</option>
                                            <option value="무승부">무승부</option>
                                        </>
                                    ) : (
                                        <option value="" disabled>경기를 먼저 선택해주세요</option>
                                    )}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">예상 스코어</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                    placeholder="예: 2-1"
                                    value={predictedScore}
                                    onChange={e => setPredictedScore(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">승리 확률 (%)</label>
                                <input 
                                    type="number" 
                                    min="0"
                                    max="100"
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                    value={winProbability}
                                    onChange={e => setWinProbability(Number(e.target.value))}
                                />
                            </div>
                        </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">분석 내용 (한국어)</label>
                        <textarea 
                            rows={12}
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none resize-none whitespace-pre-wrap"
                            placeholder="상세 분석 내용을 입력하세요"
                            value={contentKo}
                            onChange={e => setContentKo(e.target.value)}
                        />
                    </div>

                    </div>
                </div>

                <footer className="p-8 flex gap-3 shrink-0">
                    <button onClick={onClose} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">취소</button>
                    <button 
                        onClick={handleSubmit} 
                        disabled={isSubmitting || (!selectedMatch && !editId)}
                        className="flex-[2] py-4 bg-rose-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-100 md:hover:bg-rose-600 transition-all active:scale-95 disabled:opacity-50"
                    >
                        {isSubmitting ? <Loader2 className="animate-spin mx-auto" size={20} /> : (editId ? '분석글 수정' : '분석글 등록')}
                    </button>
                </footer>
            </div>
        </div>
    );
};
